import facebookIcon from "../assets/images/facebook.svg";
import instaIcon from "../assets/images/insta.svg";
import linkedinIcon from "../assets/images/linkedin.svg";
import twitterIcon from "../assets/images/twitter.svg";
import telegramIcon from "../assets/images/telegram.svg";
import youtubeIcon from "../assets/images/youtube.svg";
import ellipseIcon from "../assets/images/Ellipse.svg";

/** YYYY-MM-DD from API date / ISO string. */
export const asIsoDate = (dateInput) => {
  if (!dateInput) return "";
  const isoMatch = String(dateInput).match(/^(\d{4})-(\d{2})-(\d{2})/);
  return isoMatch ? `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}` : "";
};

/** Next calendar day YYYY-MM-DD (UTC arithmetic). */
export const nextCalendarDayIso = (yyyyMmDd) => {
  const iso = asIsoDate(yyyyMmDd);
  if (!iso) return "";
  const [y, m, d] = iso.split("-").map(Number);
  const next = new Date(Date.UTC(y, m - 1, d + 1));
  return [
    next.getUTCFullYear(),
    String(next.getUTCMonth() + 1).padStart(2, "0"),
    String(next.getUTCDate()).padStart(2, "0"),
  ].join("-");
};

/**
 * Countdown / freeze instant: end of endDate in Turkey
 * (endDate 2026-09-25 → 2026-09-26T00:00:00+03:00).
 */
export const turkeyEndOfDayIso = (endDate) => {
  const nextDay = nextCalendarDayIso(endDate);
  return nextDay ? `${nextDay}T00:00:00+03:00` : "";
};

/** Back-compat alias for older countdown import sites. */
export const turkeyStartOfDayIso = (dateInput) => {
  const iso = asIsoDate(dateInput);
  return iso ? `${iso}T00:00:00+03:00` : "";
};

/** Derive end date YYYY-MM-DD from endDate, or startDate + duration. */
export const resolveEndDateIso = ({ endDate, startDate, duration } = {}) => {
  const explicit = asIsoDate(endDate);
  if (explicit) return explicit;

  const startIso = asIsoDate(startDate);
  if (!startIso || !duration) return "";

  const match = String(duration)
    .trim()
    .match(/^(\d+)\s*(day|days|week|weeks|month|months)\s*$/i);
  if (!match) return "";

  const count = Number(match[1]);
  const unit = match[2].toLowerCase();
  const [year, month, day] = startIso.split("-").map(Number);
  const date = new Date(year, month - 1, day);
  if (Number.isNaN(date.getTime()) || !Number.isFinite(count) || count < 1) return "";

  if (unit.startsWith("day")) date.setDate(date.getDate() + count);
  else if (unit.startsWith("week")) date.setDate(date.getDate() + count * 7);
  else if (unit.startsWith("month")) date.setMonth(date.getMonth() + count);
  else return "";

  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, "0"),
    String(date.getDate()).padStart(2, "0"),
  ].join("-");
};

/** True once local calendar date is on/after competition start. */
export const hasCompetitionStarted = (startDate) => {
  const startIso = asIsoDate(startDate);
  if (!startIso) return false;
  const startMs = new Date(`${startIso}T00:00:00`).getTime();
  return Number.isFinite(startMs) && Date.now() >= startMs;
};

/** Format a date input as DD/MM/YYYY */
export const formatStartDate = (dateInput) => {
  if (dateInput instanceof Date) {
    if (Number.isNaN(dateInput.getTime())) return "";
    const day = String(dateInput.getDate()).padStart(2, "0");
    const month = String(dateInput.getMonth() + 1).padStart(2, "0");
    const year = dateInput.getFullYear();
    return `${day}/${month}/${year}`;
  }

  // Prefer YYYY-MM-DD as local date (avoids UTC off-by-one)
  const isoMatch = String(dateInput).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) {
    const [, year, month, day] = isoMatch;
    return `${day}/${month}/${year}`;
  }

  const date = new Date(dateInput);
  if (Number.isNaN(date.getTime())) return "";

  return [
    String(date.getDate()).padStart(2, "0"),
    String(date.getMonth() + 1).padStart(2, "0"),
    date.getFullYear(),
  ].join("/");
};

/** Single source of truth — change this to update the label */
export const COMPETITION_START_DATE = "2026-09-15";
export const COMPETITION_END_DATE = "2026-10-15";

const startMs = new Date(`${COMPETITION_START_DATE}T00:00:00`).getTime();
const countdownMode =
  Number.isFinite(startMs) && Date.now() >= startMs ? "end" : "start";

export const COMPETITION = {
  brandName: "GoDo",
  tagline: "GODO DEMO TRADING COMPETITION",
  title: "Showcase Your Trading Skills, Win Real Rewards!",
  subtitle:
    "Join the GODO Demo Trading Competition, trade with a $10,000 demo initial balance, outshine other traders, and claim your share of the prize pool!",
  startDate: COMPETITION_START_DATE,
  startDateLabel: `Started at ${formatStartDate(COMPETITION_START_DATE)}`,
  endDate: COMPETITION_END_DATE,
  endDateLabel: `Ends at ${formatStartDate(COMPETITION_END_DATE)}`,
  countdownMode,
  countdownTarget:
    countdownMode === "end"
      ? turkeyEndOfDayIso(COMPETITION_END_DATE)
      : `${COMPETITION_START_DATE}T00:00:00+03:00`,
  totalPrizePool: "$4,500",
  currency: "USD",
  prizesSubtitle:
    "Top three verified accounts share the full $4,500 USD pool at competition close.",
  participateUrl: "#participate",
  howItWorksUrl:
    "https://godoltd-my.sharepoint.com/:w:/g/personal/sh_godocm_com/IQCI05M7zkvsSaK0-T0UrgIJAdsD7TCkmbjNu5GpoduU0hU?web=1",
  stats: [
    { label: "Duration", value: "1 Month" },
    { label: "Starting Balance", value: "$10,000" },
    { label: "Entry", value: "Free", highlight: true },
    { label: "Accounts", value: "1 per Trader" },
  ],
};

export const SOCIAL_LINKS = [
  {
    id: "instagram",
    label: "Instagram",
    href: "https://www.instagram.com/godocapitalmarkets/",
    icon: instaIcon,
  },
  {
    id: "linkedin",
    label: "LinkedIn",
    href: "https://www.linkedin.com/company/godocm/posts/?feedView=all",
    icon: linkedinIcon,
  },
  {
    id: "facebook",
    label: "Facebook",
    href: "https://www.facebook.com/GODOCapitalMarkets/",
    icon: facebookIcon,
  },
  {
    id: "twitter",
    label: "Twitter",
    href: "https://x.com/GoDo_CM",
    icon: twitterIcon,
  },
  {
    id: "telegram",
    label: "Telegram",
    href: "https://t.me/GoDo_CM",
    icon: telegramIcon,
  },
  {
    id: "youtube",
    label: "YouTube",
    href: "https://www.youtube.com/@GoDo_CM",
    icon: youtubeIcon,
    ring: ellipseIcon,
  },
];
